import React, { useMemo, useState } from "react";
import { Users, RefreshCw, Mail, Ban } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, apiErrorMessage } from "@/lib/api";
import { BOOKING_STATUS, fmtMoney, fmtDateTime } from "@/pages/curated/shared";

const Tile = ({ label, value, testid }) => (
  <Card><CardContent className="p-4"><p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p><p className="mt-1 text-2xl font-semibold tabular-nums" data-testid={testid}>{value}</p></CardContent></Card>
);

export function CuratedBookingsTab({ trip, reload }) {
  const bookings = trip.bookings || [];
  const [cancelId, setCancelId] = useState(null);
  const sorted = useMemo(() => [...bookings].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)), [bookings]);
  const isUnpaid = (b) => ["hold", "expired"].includes(b.status) && !(b.paid_total > 0);
  const paid = bookings.filter((b) => b.status === "paid").length;
  const secured = bookings.filter((b) => b.status === "secured").length;
  const revenue = bookings.reduce((s, b) => s + (b.paid_total || 0), 0);
  const publicUrl = `${window.location.origin}/curated/${trip.public_token}`;
  const nudgeHref = (b) => {
    const first = (b.buyer_name || "").split(" ")[0] || "there";
    const body = `Hi ${first},\n\nI saw you started a reservation for ${trip.name} (${b.seats} traveller${b.seats === 1 ? "" : "s"}) but the payment wasn't completed, so your seats were released.\n\nThere's still availability — you can pick them up again here:\n${publicUrl}\n\nIf you'd rather I hold them for you or you have any questions, just reply to this email.\n\nBest,\nEspíritu Travel`;
    return `mailto:${b.buyer_email}?subject=${encodeURIComponent(`Your spot on ${trip.name}`)}&body=${encodeURIComponent(body)}`;
  };
  const cancel = async () => {
    try { await api.post(`/curated/${trip.id}/bookings/${cancelId}/cancel`); toast.success("Reserva cancelada · plazas liberadas"); reload(); }
    catch (e) { toast.error(apiErrorMessage(e)); } finally { setCancelId(null); }
  };

  if (bookings.length === 0) {
    return <div className="rounded-lg border border-dashed p-10 text-center text-sm text-muted-foreground" data-testid="curated-bookings-empty"><Users className="mx-auto mb-2 h-6 w-6" /> Aún no hay reservas. Cuando alguien reserve en la landing pública aparecerá aquí.</div>;
  }
  return (
    <div className="space-y-4" data-testid="curated-bookings-tab">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        <Tile label="Reservas" value={bookings.length} testid="curated-bk-total" />
        <Tile label="Pagadas" value={paid} testid="curated-bk-paid" />
        <Tile label="Con depósito" value={secured} testid="curated-bk-secured" />
        <Tile label="Sin pagar" value={bookings.filter(isUnpaid).length} testid="curated-bk-unpaid" />
        <Tile label="Recaudado" value={fmtMoney(revenue, trip.currency)} testid="curated-bk-revenue" />
      </div>
      <Card><CardContent className="p-0">
        <Table data-testid="curated-bookings-table">
          <TableHeader><TableRow>
            <TableHead>Comprador</TableHead><TableHead>Plazas · Hab.</TableHead><TableHead>Total</TableHead><TableHead>Pagado</TableHead><TableHead>Estado</TableHead><TableHead>Creada</TableHead><TableHead />
          </TableRow></TableHeader>
          <TableBody>
            {sorted.map((b) => {
              const rooms = (b.room_selections || []).reduce((acc, r) => ({ ...acc, [r.room_type]: (acc[r.room_type] || 0) + 1 }), {});
              const share = (b.room_selections || []).some((r) => r.willing_to_share_with_stranger);
              const st = BOOKING_STATUS[b.status] || { label: b.status, cls: "bg-zinc-100" };
              return (
                <TableRow key={b.booking_id} data-testid={`curated-booking-${b.booking_id}`}>
                  <TableCell><div className="font-medium">{b.buyer_name}</div><div className="text-xs text-muted-foreground">{b.buyer_email}{b.buyer_phone ? ` · ${b.buyer_phone}` : ""}</div></TableCell>
                  <TableCell className="text-sm tabular-nums">{b.seats} · <span className="text-xs text-muted-foreground">{Object.entries(rooms).map(([k, n]) => `${n} × ${k}`).join(" · ")}{share ? " · willing to share" : ""}</span></TableCell>
                  <TableCell className="tabular-nums">{fmtMoney(b.total_amount, b.currency)}</TableCell>
                  <TableCell className="tabular-nums">{b.paid_total > 0 ? <span className="font-medium text-emerald-700">{fmtMoney(b.paid_total, b.currency)}</span> : "—"}</TableCell>
                  <TableCell>
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${st.cls}`} data-testid={`curated-booking-status-${b.booking_id}`}>{st.label}</span>
                    {b.status === "hold" && b.hold_expires_at && <div className="mt-1 text-[11px] text-amber-700">exp {fmtDateTime(b.hold_expires_at)}</div>}
                    {isUnpaid(b) && <a href={nudgeHref(b)} className="mt-1 inline-flex items-center gap-1 text-[11px] text-primary hover:underline" data-testid={`curated-nudge-${b.booking_id}`}><Mail className="h-3 w-3" /> Escribirle</a>}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{fmtDateTime(b.created_at)}</TableCell>
                  <TableCell>{!["cancelled", "expired", "refunded"].includes(b.status) && (
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => setCancelId(b.booking_id)} aria-label="Cancelar reserva" data-testid={`curated-booking-cancel-${b.booking_id}`}><Ban className="h-4 w-4" /></Button>
                  )}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent></Card>
      <div className="text-right"><Button variant="ghost" size="sm" onClick={reload} data-testid="curated-bookings-reload"><RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Refrescar</Button></div>

      <AlertDialog open={!!cancelId} onOpenChange={(o) => !o && setCancelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Cancelar reserva</AlertDialogTitle>
            <AlertDialogDescription>Se liberan las plazas. Si la reserva tiene cobros, el reembolso se gestiona aparte en PayPal (solo Dirección puede cancelar reservas pagadas).</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Volver</AlertDialogCancel><AlertDialogAction onClick={cancel} data-testid="curated-booking-cancel-confirm">Cancelar reserva</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
